import { ButtonPrimary, ButtonSecondary, ButtonTertiary, ButtonToggle } from "@nova/component-btn"
import { HighPriorityNovaModalContainer } from "@nova/component-nova-modal-container"
import { useTranslation } from "@nova/use-translation"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { useCallback, useState } from "react"
import { selectUserEmail, selectUserID, selectUserLicense } from "user/profile/selectors"
import { createStripeCheckoutSession, LicenseDefinition, LicenseDefinitions } from "../module"
import classNames from "classnames"
import { selectUserFullName } from "features/entity-caching/members/UserManagementSlice"
import { initializeCenterModalForComponentAction } from "features/content-overlay/slice"
import { ModalNames } from "features/modals/types"
import { selectLicensePaywallType } from "features/entity-caching/licenses/selectors"
import { formatPrice } from "@nova/util-strings"
import { setLicenseUpgrade } from "features/entity-caching/licenses/actions"
import { useAsyncCallback } from "@nova/use-async-callback"
import { BillingCycle, Licenses, SubscriptionsType } from "@nova/constants"
import { hideGlobalModal } from "runlevel/actions"
import { License } from "@nova/schemas"

export const LicensePaywallModal = () => {
    const { t } = useTranslation()

    const dispatch = useAppDispatch()

    const paywallType = useAppSelector(selectLicensePaywallType())
    const currentLicense = useAppSelector(selectUserLicense)
    const currentLicenseType = currentLicense?.subscriptionType || Licenses.Lite

    const [isAnnual, setIsAnnual] = useState(true)

    const openLicenseUpgradeModal = useCallback(() => {
        dispatch(setLicenseUpgrade(currentLicense))
        dispatch(initializeCenterModalForComponentAction(ModalNames.LicenseUpgradeModal))
    }, [currentLicense, dispatch])

    const title =
        paywallType === "exports" ? "Upgrade to export layers" : "Upgrade to increase data storage"
    const subtitle =
        paywallType === "exports"
            ? "Exporting images is unavailable on the Lite plan. Upgrade from the Lite plan to unlock layer exporting."
            : "You've reached your data capacity. Select a license plan to continue."
    return (
        <HighPriorityNovaModalContainer
            title="Upgrade license"
            bottomBar={
                <div className="w-full flex justify-start items-center gap-2">
                    <ButtonTertiary title="Cancel" onClick={() => dispatch(hideGlobalModal())} />
                </div>
            }
            closeModal={() => dispatch(hideGlobalModal())}
            className="!w-auto"
        >
            <div className="text-center flex flex-col gap-2.5 justify-center items-center my-2.5 pt-2">
                <h1>{t(title)}</h1>
                <h5 className={classNames({ "max-w-[23.75rem]": paywallType === "exports" })}>
                    {t(subtitle)}
                </h5>
                <div className="flex gap-2.5 items-center">
                    <h5 className={classNames({ "text-gray-400": isAnnual })}>{t("Monthly")}</h5>
                    <ButtonToggle onToggle={() => setIsAnnual(!isAnnual)} initialValue={true} />
                    <h5 className={classNames({ "text-gray-400": !isAnnual })}>{t("Annual")}</h5>
                </div>
                <h5>{t("Save 25% with annual billing")}</h5>
            </div>
            <div className="mt-2 pt-2.5 flex gap-5">
                {currentLicense &&
                    Object.values(LicenseDefinitions).map((def, idx) => (
                        <LicenseCard
                            key={idx}
                            defintion={def}
                            isAnnual={isAnnual}
                            isCurrent={currentLicenseType === def.type}
                            isAtLimit={
                                def.type === Licenses.Lite &&
                                currentLicenseType === Licenses.Essentials
                            }
                            isPro={def.type === Licenses.Pro}
                            currentLicense={currentLicense}
                            openLicenseUpgradeModal={openLicenseUpgradeModal}
                        />
                    ))}
            </div>
        </HighPriorityNovaModalContainer>
    )
}

export const LicenseCard = ({
    defintion,
    currentLicense,
    isAnnual,
    isCurrent,
    isAtLimit,
    isPro,
    openLicenseUpgradeModal,
}: {
    defintion: LicenseDefinition
    currentLicense: License
    isAnnual: boolean
    isCurrent: boolean
    isAtLimit: boolean
    isPro: boolean
    openLicenseUpgradeModal: () => void
}) => {
    const { t } = useTranslation()

    const email = useAppSelector(selectUserEmail)
    const userId = useAppSelector(selectUserID)
    const fullName = useAppSelector(selectUserFullName())

    const { title, subtitle, pricePerMonth, pricePerYear, perks } = defintion

    const priceToUse = isAnnual ? pricePerYear / 12 : pricePerMonth
    const currentLicenseType = currentLicense.subscriptionType

    const { handleCheckout, loading: isLoading } = useAsyncCallback(
        {
            handleCheckout: async (subscriptionType: SubscriptionsType) => {
                if (!email || !userId || !fullName) return
                await createStripeCheckoutSession({
                    email,
                    userId,
                    fullName,
                    redirectUrl: encodeURIComponent(window.location.href),
                    billingCycle: isAnnual ? BillingCycle.Year : BillingCycle.Month,
                    quantity: 1,
                    subscriptionType,
                    liteLicenseId:
                        currentLicenseType === Licenses.Lite ? currentLicense.licenseId : undefined,
                })
            },
        },
        [email, userId, fullName, isAnnual, currentLicense, currentLicenseType]
    )

    return (
        <div className="relative h-[18.125rem]">
            {isPro && (
                <div className="pro-gradient-outline  absolute h-[calc(100%+2px)] w-[17.938rem] -z-50 rounded-xl -left-px -top-px" />
            )}
            <div
                className={classNames(
                    "h-full w-[17.813rem] p-6 flex flex-col gap-4 rounded-xl",
                    { "border border-gray-700": !isPro },
                    { "pro-gradient-fill": isPro }
                )}
            >
                <div>
                    <h4>{t(title)}</h4>
                    <h5 className="text-gray-400">{t(subtitle)}</h5>
                </div>
                <div>
                    <div className="flex">
                        <h2 className="font-medium mono">{t(formatPrice(priceToUse))}</h2>
                        <small className="self-end mb-0.5">{t("/mo")}</small>
                    </div>
                    {!!pricePerYear && (
                        <>
                            {isAnnual && (
                                <>
                                    <small className="text-gray-300">
                                        {t(`USD - billed annually `)}
                                    </small>
                                    <small className="mono text-gray-300">
                                        {formatPrice(pricePerYear)}
                                    </small>
                                </>
                            )}
                            {!isAnnual && (
                                <small className="text-gray-300">{t(`USD - billed monthly`)}</small>
                            )}
                        </>
                    )}
                    {!pricePerYear && <small className="text-gray-300">{t("Free")}</small>}
                </div>
                <ul className="list-disc pl-4 mt-0.5">
                    {perks.map((perk, idx) => (
                        <li key={idx} className="text-gray-300">
                            <h5>{t(perk)}</h5>
                        </li>
                    ))}
                </ul>
                <div className="grow" />
                {!isCurrent && !isAtLimit && (
                    <ButtonPrimary
                        title="Subscribe"
                        className="w-full"
                        onClick={
                            currentLicenseType === Licenses.Lite
                                ? () => handleCheckout(defintion.type)
                                : () => openLicenseUpgradeModal()
                        }
                        isLoading={isLoading}
                    />
                )}
                {isCurrent && <ButtonSecondary title="Current" className="w-[4.5rem]" isReadOnly />}
                {isAtLimit && (
                    <ButtonTertiary title="Limit Reached" className="w-[7.5rem]" isReadOnly />
                )}
            </div>
        </div>
    )
}
